import { FormikHelpers } from 'formik'

import EfiPay from 'payment-token-efi'

import { HttpError } from '@/api/http'
import { createPagarmeCardToken } from '@/api/pagarme/tokens'
import { useMutationPostPaymentCreditCard } from '@/api/payment/mutations'
import { getPaymentCardGateway } from '@/api/payment/services'
import { useToast } from '@/contexts/ToastContext'

import { CardFormData } from './types'

const getPagarmePublicKey = async (): Promise<string | null> => {
  const { pagarme } = await getPaymentCardGateway()
  return pagarme.enabled ? pagarme.public_key : null
}

const maskCardNumber = (cardNumber: string): string => `${'X'.repeat(cardNumber.length - 4)}${cardNumber.slice(-4)}`

export const useCardAddFormSubmit = (onSuccess: (cardId?: number) => void, replacesCardId?: number) => {
  const { show } = useToast()
  const { mutateAsync: mutatePostPaymentCreditCard } = useMutationPostPaymentCreditCard()

  const handleSubmit = async (values: CardFormData, { setSubmitting }: FormikHelpers<CardFormData>) => {
    setSubmitting(true)

    try {
      const cardBrand = await EfiPay.CreditCard.setCardNumber(values.card_number).verifyCardBrand()

      if (cardBrand === 'undefined' || cardBrand === 'unknown') {
        show('O cartão informado é inválido!', { severity: 'error' })
        return
      }

      const efiAccount = process.env.REACT_APP_EFI_ACCOUNT
      const efiEnvironment = process.env.REACT_APP_EFI_ENV || 'sandbox'

      if (!efiAccount) {
        show('Ocorreu um erro ao obter os dados para adicionar o cartão!', { severity: 'error' })
        return
      }

      const pagarmePublicKey = await getPagarmePublicKey()

      const cardNumber = values.card_number.replace(/\s/g, '')
      const [expirationMonth, expirationYearSuffix] = values.card_expiration.split('/')
      const expirationYear = `${new Date().getFullYear().toString().slice(0, 2)}${expirationYearSuffix}`

      let efiError: unknown
      const efiResponse = await EfiPay.CreditCard.setAccount(efiAccount)
        .setEnvironment(efiEnvironment)
        .setCreditCardData({
          brand: cardBrand,
          number: cardNumber,
          cvv: values.card_cvv,
          expirationMonth,
          expirationYear,
          reuse: true,
          holderDocument: values.cpf.replace(/\D/g, ''),
          holderName: values.name,
        })
        .getPaymentToken()
        .then((response) => response as EfiPay.CreditCard.PaymentTokenResponse)
        .catch((error: unknown) => {
          efiError = error
          return undefined
        })

      const pagarmeCardToken = pagarmePublicKey
        ? await createPagarmeCardToken(pagarmePublicKey, {
            number: cardNumber,
            holderName: values.name,
            expMonth: Number(expirationMonth),
            expYear: Number(expirationYear),
            cvv: values.card_cvv,
          }).catch(() => undefined)
        : undefined

      const pagarmeMissing = Boolean(pagarmePublicKey) && !pagarmeCardToken
      if (!efiResponse && !pagarmeCardToken) {
        throw efiError ?? new Error('Card tokenization failed')
      }
      if (replacesCardId && (!efiResponse || pagarmeMissing)) {
        throw efiError ?? new Error('Card tokenization failed')
      }

      const payment_token = efiResponse?.payment_token
      const card_mask = efiResponse?.card_mask ?? maskCardNumber(cardNumber)

      const { id: cardId } = await mutatePostPaymentCreditCard({
        token: payment_token,
        pagarme_card_token: pagarmeCardToken,
        replaces_card_id: replacesCardId,
        brand: cardBrand,
        cpf: values.cpf.replace(/\D/g, ''),
        name: values.name,
        card_mask,
        expiration_month: expirationMonth,
        expiration_year: expirationYearSuffix,
        address_street: values.address_street,
        address_number: values.address_number,
        address_neighborhood: values.address_neighborhood,
        address_zip: values.address_zip.replace(/\D/g, ''),
        address_city: values.address_city,
        address_state: values.address_state,
      })

      show(replacesCardId ? 'Cartão recadastrado com sucesso!' : 'Cartão adicionado com sucesso!', {
        severity: 'success',
      })

      onSuccess(cardId)
    } catch (error: unknown) {
      const err = error as EfiPay.CreditCard.ErrorResponse

      if (error instanceof HttpError) {
        show(error.message, { severity: 'error' })
      } else if (err.error_description) {
        show(err.error_description, { severity: 'error' })
      } else {
        show('Ocorreu um erro ao adicionar o cartão!', { severity: 'error' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return handleSubmit
}
